import ChartModal from '@/components/ChartModal';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { filterExcludedTitles } from '@/config/excludedTitles';
import { supabase } from '@/lib/supabase';
import type { MusicData } from '@/types/music';
import { ArrowLeft, Calendar, ChevronDown, Music, Snowflake, Trophy, Youtube } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ChristmasData {
  year: string;
  songs: MusicData[];
}

interface TopSong {
  title: string;
  count: number;
  years: string[];
  artist: string;
}

const ChristmasPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [christmasData, setChristmasData] = useState<ChristmasData[]>([]);
  const [topSongs, setTopSongs] = useState<TopSong[]>([]);
  const [loadingChristmas, setLoadingChristmas] = useState<boolean>(true);
  const [hasDecemberData, setHasDecemberData] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedSong, setSelectedSong] = useState<{ title: string; artist: string } | null>(null);
  const [isChartModalOpen, setIsChartModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  // Load saved date from localStorage on initial render
  useEffect(() => {
    const savedDate = localStorage.getItem('selectedDate');
    if (savedDate) {
      setSelectedDate(savedDate);
    }
  }, []);

  // Fetch Christmas data (December 25th) for all years
  useEffect(() => {
    const fetchChristmasData = async () => {
      setLoadingChristmas(true);
      setErrorMessage('');
      setHasDecemberData(false);

      try {
        let data: MusicData[] | null = null;

        console.log('🎄 Starting Christmas data fetch...');

        // Method 1: Try to fetch from christmas_songs_view (recommended)
        console.log('📊 Fetching from christmas_songs_view...');
        const { data: viewData, error: viewError } = await supabase
          .from('christmas_songs_view')
          .select('*')
          .order('broadcast_date', { ascending: false })
          .order('number', { ascending: true });

        if (!viewError && viewData && viewData.length > 0) {
          data = viewData;
          console.log('✅ View query succeeded, count:', viewData.length);
        } else {
          console.log('⚠️ View query failed, trying RPC function...', viewError);

          // Method 2: Fallback to RPC function
          const { data: rpcData, error: rpcError } = await supabase.rpc('get_christmas_songs');

          if (!rpcError && rpcData && rpcData.length > 0) {
            data = rpcData;
            console.log('✅ RPC function succeeded, count:', rpcData.length);
          } else {
            console.log('⚠️ RPC failed, trying direct query...', rpcError);

            // Method 3: Fallback to direct query with December data filter
            console.log('📅 Fetching all December data to filter...');
            const { data: allDecemberData, error: decError } = await supabase
              .from('music_data')
              .select('*')
              .like('broadcast_date', '%-12-%')
              .order('broadcast_date', { ascending: false })
              .order('number', { ascending: true });

            if (decError) {
              console.error('❌ Error fetching December data:', decError);
              setErrorMessage('12월 데이터를 조회하는 중 오류가 발생했습니다.');
            } else if (allDecemberData && allDecemberData.length > 0) {
              console.log('✅ Found December data:', allDecemberData.length, 'items');
              setHasDecemberData(true);

              // Filter for December 25th
              const christmasItems = allDecemberData.filter((item) => {
                if (!item.broadcast_date) return false;

                // Convert to string and check for -12-25 pattern
                const dateStr = String(item.broadcast_date);

                // Check multiple patterns
                if (dateStr.includes('-12-25') || dateStr.endsWith('-12-25')) {
                  return true;
                }

                // Also try Date object parsing
                try {
                  const date = new Date(item.broadcast_date);
                  if (!Number.isNaN(date.getTime())) {
                    const month = date.getMonth() + 1; // 0-11 -> 1-12
                    const day = date.getDate();
                    if (month === 12 && day === 25) {
                      return true;
                    }
                  }
                } catch (e) {
                  // Ignore parsing errors
                }

                return false;
              });

              console.log('🎄 December 25th items found:', christmasItems.length);

              if (christmasItems.length > 0) {
                data = christmasItems;
                console.log('✅ Successfully filtered Christmas data');
              } else {
                console.log('❌ No December 25th data found in December data');
                setErrorMessage('데이터베이스에 12월 데이터는 있지만 12월 25일 데이터가 없습니다.');
              }
            } else {
              console.log('❌ No December data found at all');
              setErrorMessage('데이터베이스에 12월 데이터가 없습니다.');
            }
          }
        }

        if (data && data.length > 0) {
          // Filter excluded titles
          const filteredData = filterExcludedTitles(data);
          console.log(
            'Filtered data (after excluding titles):',
            filteredData.length,
            'from',
            data.length
          );

          // Calculate top songs (by uppercase title)
          const songCountMap = new Map<string, { count: number; years: Set<string> }>();

          for (const item of filteredData) {
            if (item.title && item.broadcast_date) {
              const upperTitle = item.title.toUpperCase();
              const year = item.broadcast_date.split('-')[0];

              if (!songCountMap.has(upperTitle)) {
                songCountMap.set(upperTitle, { count: 0, years: new Set() });
              }

              const songData = songCountMap.get(upperTitle);
              if (songData) {
                songData.count++;
                songData.years.add(year);
              }
            }
          }

          // Convert to array and sort by count, get top 10
          // Also find the artist for each song from the data
          const topSongsArray: TopSong[] = Array.from(songCountMap.entries())
            .map(([title, { count, years }]) => {
              // Find the first occurrence of this song to get artist
              // Try exact match first, then case-insensitive match
              const firstOccurrence = filteredData.find((item) => {
                if (!item.title) return false;
                return item.title.toUpperCase() === title || item.title === title;
              });

              const songTitle = firstOccurrence?.title || title;
              const songArtist = firstOccurrence?.artist || '';

              // Validate that we have both title and artist
              if (!songTitle || !songArtist) {
                console.warn('Missing data for song:', {
                  title: songTitle,
                  artist: songArtist,
                  originalTitle: title,
                });
              }

              return {
                title: songTitle,
                count,
                years: Array.from(years).sort(
                  (a, b) => Number.parseInt(b, 10) - Number.parseInt(a, 10)
                ),
                artist: songArtist,
              };
            })
            .filter((song) => song.title && song.artist) // Filter out songs without artist
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);

          setTopSongs(topSongsArray);
          console.log('Top 10 Christmas songs:', topSongsArray);

          // Group by year
          const groupedByYear = new Map<string, MusicData[]>();

          for (const item of filteredData) {
            if (item.broadcast_date) {
              const year = item.broadcast_date.split('-')[0];
              if (!groupedByYear.has(year)) {
                groupedByYear.set(year, []);
              }
              const yearSongs = groupedByYear.get(year);
              if (yearSongs) {
                yearSongs.push(item);
              }
            }
          }

          // Convert to array and sort by year descending
          // Re-index song numbers after filtering
          const christmasArray: ChristmasData[] = Array.from(groupedByYear.entries())
            .map(([year, songs]) => {
              // Sort by original number first
              const sortedSongs = songs.sort((a, b) => (a.number || 0) - (b.number || 0));
              // Re-index from 1
              const reIndexedSongs = sortedSongs.map((song, index) => ({
                ...song,
                number: index + 1,
              }));
              return {
                year,
                songs: reIndexedSongs,
              };
            })
            .sort((a, b) => Number.parseInt(b.year, 10) - Number.parseInt(a.year, 10));

          console.log('Final Christmas data grouped by year:', christmasArray);
          setChristmasData(christmasArray);
        } else {
          console.log('❌ No Christmas data found after all attempts');
          console.log('This could mean:');
          console.log('1. There is no December 25th data in the database');
          console.log('2. The data format is different than expected');
          console.log('3. There is a query/permission issue');
          setChristmasData([]);
          setTopSongs([]);
          setErrorMessage(
            '크리스마스(12월 25일) 데이터를 찾을 수 없습니다. 데이터베이스에 해당 날짜의 데이터가 없을 수 있습니다.'
          );
        }
      } catch (error) {
        console.error('Failed to fetch Christmas data:', error);
        setChristmasData([]);
        setTopSongs([]);
      } finally {
        setLoadingChristmas(false);
      }
    };

    fetchChristmasData();
    // errorMessage is set inside the effect, so we don't need it as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    localStorage.setItem('selectedDate', date);
  };

  const handleSearchResultClick = (title: string, artist: string) => {
    // Handle search result click if needed
  };

  return (
    <div className="min-h-screen">
      <Header onDateChange={handleDateChange} onSearchResultClick={handleSearchResultClick} />

      <main className="container mx-auto max-w-7xl px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <button
            type="button"
            onClick={() => navigate('/chronology')}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-400" />
          </button>
          <div className="flex items-center gap-3">
            <Snowflake className="h-7 w-7 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Christmas Special</h1>
            <Snowflake className="h-7 w-7 text-blue-400" />
          </div>
        </div>

        {/* Top 10 Songs Section */}
        {!loadingChristmas && topSongs.length > 0 && (
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 shadow-lg mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Trophy className="h-6 w-6 text-yellow-400" />
              <h2 className="text-2xl font-bold text-white">크리스마스 인기 곡 Top 10</h2>
            </div>
            <p className="text-gray-400 mb-4 text-sm">
              크리스마스에 가장 많이 재생된 곡들 (대문자 기준, 제외된 곡 제외)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {topSongs.map((song, index) => (
                <div
                  key={song.title}
                  className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 hover:bg-gray-900/70 transition-colors w-full"
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (song.title && song.artist) {
                        setSelectedSong({ title: song.title, artist: song.artist });
                        setIsChartModalOpen(true);
                      } else {
                        console.warn('Song data incomplete:', {
                          title: song.title,
                          artist: song.artist,
                        });
                      }
                    }}
                    className="w-full text-left cursor-pointer"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={`text-lg font-bold ${
                          index === 0
                            ? 'text-yellow-400'
                            : index === 1
                              ? 'text-gray-300'
                              : index === 2
                                ? 'text-amber-600'
                                : 'text-gray-400'
                        }`}
                      >
                        #{index + 1}
                      </span>
                    </div>
                    <p className="text-sm text-white font-medium mb-1 truncate">{song.title}</p>
                    {song.artist && (
                      <p className="text-xs text-gray-400 mb-2 truncate">{song.artist}</p>
                    )}
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Music className="h-3 w-3" />
                      <span>{song.count}회</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {song.years.slice(0, 3).join(', ')}
                      {song.years.length > 3 && ` +${song.years.length - 3}`}
                    </div>
                  </button>
                  <div className="flex justify-end mt-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(
                                `https://www.youtube.com/results?search_query=${encodeURIComponent(
                                  `${song.artist} ${song.title}`
                                )}`,
                                '_blank'
                              );
                            }}
                            className="text-muted-foreground hover:text-primary flex-shrink-0 h-6 w-6 p-0"
                          >
                            <Youtube className="h-4 w-4 text-[#FF0000]" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Search on YouTube</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Christmas Section */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 shadow-lg mb-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <p className="text-gray-400 text-center sm:text-left">
              매년 12월 25일 크리스마스에 들었던 노래들
            </p>
            {/* Year Filter Dropdown */}
            {!loadingChristmas && christmasData.length > 0 && (
              <div className="flex items-center gap-2">
                <label
                  htmlFor="year-select"
                  className="text-white font-medium text-sm flex items-center gap-2"
                >
                  <Calendar className="h-4 w-4 text-blue-400" />
                  년도:
                </label>
                <div className="relative">
                  <select
                    id="year-select"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm appearance-none cursor-pointer hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]"
                  >
                    <option value="all">전체 년도</option>
                    {christmasData.map(({ year }) => (
                      <option key={year} value={year}>
                        {year}년
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            )}
          </div>

          {loadingChristmas ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
            </div>
          ) : christmasData.length === 0 ? (
            <div className="text-center py-8">
              <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-6 mb-4">
                <p className="text-yellow-400 font-semibold mb-2">
                  크리스마스 데이터를 찾을 수 없습니다
                </p>
                {errorMessage ? (
                  <p className="text-gray-300 text-sm mb-4">{errorMessage}</p>
                ) : (
                  <p className="text-gray-300 text-sm mb-4">
                    데이터베이스에 12월 25일 데이터가 없습니다.
                  </p>
                )}
                {hasDecemberData && (
                  <p className="text-gray-400 text-xs mt-2">
                    💡 12월 데이터는 있지만 12월 25일 데이터가 없습니다. 다른 날짜의 데이터를
                    확인해보세요.
                  </p>
                )}
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 text-left">
                <p className="text-gray-400 text-sm mb-2">가능한 원인:</p>
                <ul className="text-gray-500 text-xs space-y-1 list-disc list-inside">
                  <li>데이터베이스에 12월 25일 방송 데이터가 없음</li>
                  <li>날짜 형식이 예상과 다를 수 있음</li>
                  <li>쿼리 권한 문제일 수 있음</li>
                </ul>
                <p className="text-gray-400 text-xs mt-4">
                  브라우저 콘솔에서 상세한 디버깅 정보를 확인할 수 있습니다.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {christmasData
                .filter(({ year }) => selectedYear === 'all' || year === selectedYear)
                .map(({ year, songs }) => (
                  <div
                    key={year}
                    className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 hover:bg-gray-900/70 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar className="h-5 w-5 text-red-400" />
                      <h3 className="text-xl font-semibold text-white">{year}년 12월 25일</h3>
                      <span className="text-sm text-gray-400">({songs.length}곡)</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {songs.map((song) => (
                        <div
                          key={`${year}-${song.seqid || song.number || song.title}`}
                          className="flex items-center gap-3 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800/80 border border-gray-700/50 hover:border-gray-600 transition-all w-full group"
                        >
                          <button
                            type="button"
                            onClick={() => {
                              if (song.title && song.artist) {
                                setSelectedSong({ title: song.title, artist: song.artist });
                                setIsChartModalOpen(true);
                              } else {
                                console.warn('Song data incomplete:', {
                                  title: song.title,
                                  artist: song.artist,
                                });
                              }
                            }}
                            className="flex items-center gap-3 flex-1 text-left cursor-pointer min-w-0"
                            disabled={!song.title || !song.artist}
                          >
                            <Music className="h-6 w-6 text-blue-400 flex-shrink-0 group-hover:text-blue-300 transition-colors" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate font-medium group-hover:text-blue-200 transition-colors">
                                {song.title}
                              </p>
                              <p className="text-xs text-gray-400 truncate mt-0.5 group-hover:text-gray-300 transition-colors">
                                {song.artist}
                              </p>
                            </div>
                          </button>
                          {song.title && song.artist && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(
                                        `https://www.youtube.com/results?search_query=${encodeURIComponent(
                                          `${song.artist} ${song.title}`
                                        )}`,
                                        '_blank'
                                      );
                                    }}
                                    className="text-muted-foreground hover:text-primary flex-shrink-0 h-6 w-6 p-0"
                                  >
                                    <Youtube className="h-4 w-4 text-[#FF0000]" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Search on YouTube</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Chart Modal */}
        {selectedSong?.title && selectedSong?.artist && (
          <ChartModal
            isOpen={isChartModalOpen}
            onClose={() => {
              setIsChartModalOpen(false);
              setSelectedSong(null);
            }}
            title={selectedSong.title}
            artist={selectedSong.artist}
          />
        )}
      </main>
    </div>
  );
};

export default ChristmasPage;
